import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Sparkles, Calendar as CalendarIcon, X, Loader2, Users, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { SingleDayFilter, QuickRangeButtons } from '../components/dashboard/FilterComponents';
import { ExtraInvoiceRow } from '../components/extra/ExtraInvoiceRow';
import { ReassignUserDialog } from '../components/admin/ReassignUserDialog';
import { toastDeletedWithUndo } from '../lib/undoDelete';

const API = process.env.REACT_APP_BACKEND_URL || '';
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const DatePick = ({ value, onChange, label, testId }) => (
    <Popover>
        <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid={testId}>
                <CalendarIcon className="w-4 h-4" />
                {value ? format(value, 'dd MMM yyyy', { locale: es }) : label}
            </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={value} onSelect={onChange} locale={es} />
        </PopoverContent>
    </Popover>
);

export default function ExtraAccounting() {
    const { getAuthHeaders } = useAuth();
    const [data, setData] = useState(null);
    const [reassign, setReassign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [userId, setUserId] = useState('');
    const [searchName, setSearchName] = useState('');
    const [searchAmount, setSearchAmount] = useState('');
    const [searchDay, setSearchDay] = useState(null);
    const [applied, setApplied] = useState({ name: '', amount: '', day: null });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
            if (dateTo) params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
            if (userId) params.append('user_id', userId);
            if (applied.name) params.append('name', applied.name);
            if (applied.amount) params.append('amount', applied.amount);
            if (applied.day) params.append('date', format(applied.day, 'yyyy-MM-dd'));
            const res = await axios.get(`${API}/api/admin/extra-accounting?${params}`, { headers: getAuthHeaders() });
            setData(res.data);
        } catch (e) {
            toast.error('Error al cargar la contabilidad extra');
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, userId, applied, getAuthHeaders]);

    useEffect(() => { load(); }, [load]);

    const hasFilters = dateFrom || dateTo || userId || applied.name || applied.amount || applied.day;

    const handleAmountUpdate = async (invoiceId, newAmount) => {
        try {
            await axios.patch(`${API}/api/invoices/${invoiceId}/amount`, { amount: newAmount }, { headers: getAuthHeaders() });
            toast.success(`Monto actualizado a $${newAmount.toFixed(2)}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar el monto');
        }
    };

    const handleCategoryUpdate = async (invoiceId, newCategory) => {
        try {
            const res = await axios.put(`${API}/api/invoices/${invoiceId}/category`, { category: newCategory }, { headers: getAuthHeaders() });
            const d = res.data;
            const feeNote = d.old_amount !== undefined && d.old_amount !== d.amount ? ` (monto: $${d.old_amount.toFixed(2)} → $${d.amount.toFixed(2)})` : '';
            toast.success(`Categoría cambiada a ${d.category}${feeNote}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar la categoría');
        }
    };

    const handleDelete = async (inv) => {
        if (!window.confirm(`¿Eliminar la factura "${inv.name}" (${money(inv.amount)}) de ${inv.user_name}? Se borrará también de Extracciones y del registro.`)) return;
        try {
            await axios.delete(`${API}/api/invoices/${inv.id}`, { headers: getAuthHeaders() });
            toastDeletedWithUndo({ invoiceId: inv.id, label: `${inv.name} · ${money(inv.amount)}`, headers: getAuthHeaders(), onRestored: load });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al eliminar la factura');
        }
    };
    const applySearch = (e) => { e?.preventDefault(); setApplied({ name: searchName.trim(), amount: searchAmount, day: searchDay }); };
    const clearAll = () => { setDateFrom(null); setDateTo(null); setUserId(''); setSearchName(''); setSearchAmount(''); setSearchDay(null); setApplied({ name: '', amount: '', day: null }); };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="extra-accounting-page">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-fuchsia-600 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    Contabilidad Extra
                </h1>
                <p className="text-slate-500 mt-1 text-sm">
                    Facturas de usuarios marcados como "extra". No entran en Totales, Facturas ni Dashboard; en Extracciones aparecen bajo "Extras".
                </p>
            </div>

            <Card className="border-0 shadow-sm">
                <CardContent className="pt-4 space-y-3">
                <form onSubmit={applySearch} className="flex flex-wrap items-center gap-2" data-testid="extra-search-form">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                        <Input value={searchName} onChange={(e) => setSearchName(e.target.value)} placeholder="Buscar por nombre" className="h-9 pl-8 w-48" data-testid="extra-search-name" />
                    </div>
                    <Input type="number" step="0.01" min="0" value={searchAmount} onChange={(e) => setSearchAmount(e.target.value)} placeholder="Monto (±$0.50)" className="h-9 w-36" data-testid="extra-search-amount" />
                    <DatePick value={searchDay} onChange={setSearchDay} label="Día" testId="extra-search-day" />
                    <Button type="submit" size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="extra-search-btn">
                        <Search className="w-4 h-4 mr-1" /> Buscar
                    </Button>
                </form>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-400">Rango:</span>
                    <DatePick value={dateFrom} onChange={setDateFrom} label="Desde" testId="extra-date-from" />
                    <DatePick value={dateTo} onChange={setDateTo} label="Hasta" testId="extra-date-to" />
                    <SingleDayFilter
                        day={dateFrom && dateTo && dateFrom.getTime() === dateTo.getTime() ? dateFrom : null}
                        onSelectDay={(d) => { setDateFrom(d); setDateTo(d); }}
                        testId="extra-single-day-btn"
                    />
                    <QuickRangeButtons
                        onSelectRange={(from, to) => { setDateFrom(from); setDateTo(to); }}
                        activeFrom={dateFrom}
                        activeTo={dateTo}
                        testIdPrefix="extra"
                    />
                    <select
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                        data-testid="extra-user-select"
                    >
                        <option value="">Todos los usuarios extra</option>
                        {data?.users?.map(u => <option key={u.user_id} value={u.user_id}>{u.user_name}</option>)}
                    </select>
                    {hasFilters && (
                        <Button variant="ghost" size="sm" onClick={clearAll} data-testid="extra-clear-btn">
                            <X className="w-4 h-4 mr-1" /> Limpiar
                        </Button>
                    )}
                </div>
                </CardContent>
            </Card>

            {loading || !data ? (
                <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" /></div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: 'Confirmado', value: money(data.total_confirmed), sub: `${data.count_confirmed} facturas`, cls: 'text-green-700', id: 'extra-total-confirmed' },
                            { label: 'Pendiente', value: money(data.total_pending), sub: `${data.count_pending} facturas`, cls: 'text-amber-700', id: 'extra-total-pending' },
                            { label: 'Total', value: money(data.total_confirmed + data.total_pending), sub: `${data.count_confirmed + data.count_pending} facturas`, cls: 'text-slate-800', id: 'extra-total-all' },
                            { label: 'Usuarios extra', value: data.users.length, sub: 'marcados en Usuarios', cls: 'text-fuchsia-700', id: 'extra-users-count' },
                        ].map(c => (
                            <Card key={c.id} className="border-0 shadow-sm"><CardContent className="pt-4">
                                <p className="text-xs text-slate-500">{c.label}</p>
                                <p className={`text-2xl font-bold ${c.cls}`} data-testid={c.id}>{c.value}</p>
                                <p className="text-xs text-slate-400">{c.sub}</p>
                            </CardContent></Card>
                        ))}
                    </div>

                    <Card className="border-0 shadow-sm">
                        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-fuchsia-600" /> Por usuario</CardTitle></CardHeader>
                        <CardContent>
                            {data.users.length === 0 ? (
                                <p className="text-sm text-slate-400 py-6 text-center">Ningún usuario marcado como extra. Márcalos en Usuarios → menú → "Marcar usuario extra".</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead><tr className="text-left text-slate-500 border-b">
                                            <th className="py-2 px-2">Usuario</th><th className="py-2 px-2 text-right">Confirmado</th><th className="py-2 px-2 text-right">Pendiente</th><th className="py-2 px-2 text-right">Total</th>
                                        </tr></thead>
                                        <tbody>
                                            {data.users.map(u => (
                                                <tr key={u.user_id} className="border-b border-slate-100" data-testid={`extra-user-row-${u.user_id}`}>
                                                    <td className="py-2 px-2 font-medium">{u.user_name}<span className="block text-xs text-slate-400">{u.email}</span></td>
                                                    <td className="py-2 px-2 text-right text-green-700 font-semibold">{money(u.confirmed_total)} <span className="text-xs text-slate-400">({u.confirmed_count})</span></td>
                                                    <td className="py-2 px-2 text-right text-amber-700">{money(u.pending_total)} <span className="text-xs text-slate-400">({u.pending_count})</span></td>
                                                    <td className="py-2 px-2 text-right font-bold">{money(u.confirmed_total + u.pending_total)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-sm">
                        <CardHeader className="pb-2"><CardTitle className="text-base">Facturas ({data.invoices.length})</CardTitle></CardHeader>
                        <CardContent>
                            {data.invoices.length === 0 ? (
                                <p className="text-sm text-slate-400 py-6 text-center">Sin facturas en este periodo</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead><tr className="text-left text-slate-500 border-b">
                                            <th className="py-2 px-2">Fecha contable</th><th className="py-2 px-2">Nombre</th><th className="py-2 px-2">Usuario</th><th className="py-2 px-2">Cat.</th><th className="py-2 px-2 text-right">Monto</th><th className="py-2 px-2">Estado</th><th className="py-2 px-2 text-right">Acciones</th>
                                        </tr></thead>
                                        <tbody>
                                            {data.invoices.map(inv => (
                                                <ExtraInvoiceRow
                                                    key={inv.id}
                                                    inv={inv}
                                                    money={money}
                                                    onAmountUpdate={handleAmountUpdate}
                                                    onCategoryUpdate={handleCategoryUpdate}
                                                    onDelete={handleDelete}
                                                    onReassign={(i) => setReassign({ invoiceId: i.id, invoiceName: i.name, amount: i.amount, currentUserName: i.user_name })}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
            <ReassignUserDialog target={reassign} onClose={() => setReassign(null)} onDone={() => load()} />
        </div>
    );
}
